import { ArrowBackIos, Attachment, MoreVert, ReportRounded, Send } from '@mui/icons-material';
import { IconButton, TextField } from '@mui/material';
import { addHours } from 'date-fns';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useBans } from '../../../../../hooks/useBans';
import useMessaging from '../../../../../hooks/useMessaging';
import { useRole } from '../../../../../hooks/useRole';
import { AttendeeMessage, MessagingConversation } from '../../../../../interfaces/interfaces';
import { fetchAndSetAttendeeMessaging, setAttendeeMessaging, setAttendeeNewMessage } from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../../reduxToolkit/typedRedux';
import { seeAttendeeMessageService } from '../../../../../services/services';
import { VyConfirm } from '../../../../common/VyConfirm';
import VyPopoverMenu from '../../../../common/VyPopoverMenu';
import VyTooltip from '../../../../common/VyTooltip';
import ChatEmojis from '../../../chatEmojis';
import AttendeeAvatar from '../AttendeeAvatar';
import { AttendeeCallButton } from './AttendeeCall/AttendeeCallButton';
import { Message } from './Message';
import MessagingAttachment from './MessagingAttachment';
import { useReportUser } from '../../../../meeting/hooks/useReportUser';
import { store } from '../../../../../reduxToolkit/store';
import { FaHandshake } from 'react-icons/fa';

interface conversationComponentInterface {
  onReturnClick?: () => void;
  conversation: MessagingConversation;
  showHeaderButtons?: boolean;
}

export interface AttachmentInterface extends File {
  fileId?: string;
}

const ConversationComponent = ({ conversation, showHeaderButtons = true, onReturnClick }: conversationComponentInterface) => {
  // this state is only used to disable sending empty messages
  const [attachments, setAttachments] = useState<AttachmentInterface[]>([]);
  const [sortedMessages, setSortedMessages] = useState<AttendeeMessage[]>([]);

  const [fileIds, setFileIds] = useState<string[]>([]);
  const msgInputRef = useRef<any>(null);
  const conversationRef = useRef<any>(null);
  const fileInputRef = useRef<any>(null);
  const event = useSelector(({ common }) => common.event);
  const registration = useSelector(({ common }) => common.eventRegistration);
  const user = useSelector(({ common }) => common.user);
  const eventChannel = useSelector(({ common }) => common.pusherChannels.eventChannel);
  const messaging = useSelector(({ common }) => common.attendeeMessaging);
  const userbans = useSelector(({ common }) => common.userbans);
  const meeting = useSelector(({ video }) => video.meeting);

  const attendeeKey = user?._id === conversation.userA._id ? 'userB' : 'userA';
  const attendee = conversation[attendeeKey];
  const _attendee = { ...attendee, backgroundColor: conversation.bgColor };

  const { t } = useTranslation();
  const { showReportModal } = useReportUser();
  const { filterAndSortMessagesForConversation } = useMessaging();

  const { handleSubmit, setValue, watch, register } = useForm({ mode: 'onChange' });
  const dispatch = useDispatch();
  const { isBannedFromMessaging } = useBans();
  const { isUserEventHost } = useRole();

  const isBanned = userbans ? isBannedFromMessaging(userbans) : false;
  const isReported = messaging?.reporters?.includes(attendee._id) ?? false;

  const submit = async (props: any) => {
    if (!props.message && fileIds.length === 0) return;
    if (fileIds.length !== attachments.length) return;
    sendMessage(props.message);
  };

  function sendMessage(message?: string, messageType = 'MESSAGE') {
    if (!event) return;
    if (!messaging) return;
    if (!user) return;
    try {
      // get the current pending messages in redux
      const pendingMessages = messaging.messages?.filter((m) => m.isPending);

      // add a new local message, with temporary data and isPending set to true
      dispatch(
        setAttendeeNewMessage({
          _id: `pending-${messaging.messages?.length ?? 0}`,
          datetime_created: addHours(new Date(), pendingMessages?.length || 0),
          datetime_seen: addHours(new Date(), pendingMessages?.length || 0),
          message: message,
          fileIds: fileIds,
          messageType,
          sender: user?._id,
          recepient: attendee._id,
          event: event._id,
          isPending: true,
        })
      );
      if (messageType === 'MESSAGE') {
        setValue('message', '');
        setFileIds([]);
        setAttachments([]);
      }
    } catch (error) {
      console.log('ERROR : ', error);
    }
  }

  const seeMessages = async () => {
    if (!event || !attendee) return;
    const isNew = sortedMessages.filter((m) => m.sender === attendee?._id).find((m) => m.datetime_seen === null);
    if (!!isNew) {
      await seeAttendeeMessageService(event._id, attendee?._id);

      // for some reason it uses the previous state when I use the useSelector hook value
      const _messages = store.getState().common.attendeeMessaging?.messages?.map((m) => ({ ...m, datetime_seen: new Date() }));
      dispatch(setAttendeeMessaging({ ...messaging, messages: _messages }));
    }
  };

  // if the attendee of the conversation has not been fetched to the redux state
  // append it since we need only one source of truth
  const appendAttendeeIfNotPresent = () => {
    if (!messaging || !messaging.attendees) return;
    if (!messaging.attendees.find((a) => a._id === attendee._id))
      dispatch(setAttendeeMessaging({ ...messaging, attendees: [...messaging.attendees, attendee] }));
  };

  useEffect(() => {
    if (!attendee) return;
    appendAttendeeIfNotPresent();
  }, [attendee, messaging]);

  useEffect(() => {
    if (messaging) return;
    if (!event) return;
    dispatch(fetchAndSetAttendeeMessaging(event._id));
  }, [messaging, event]);

  useEffect(() => {
    if (!sortedMessages || !conversation) return;
    seeMessages();
  }, [sortedMessages]);

  useEffect(() => {
    setSortedMessages(filterAndSortMessagesForConversation(conversation.userA._id, conversation.userB._id));
  }, [conversation, messaging?.messages]);

  useEffect(() => {
    eventChannel?.bind('user-connect', (data: { userId: string }) => {
      if (data.userId === attendee?._id) {
        const _attendees = messaging?.attendees?.map((a) => (a._id === data.userId ? { ...a, datetimeLastRealtimeConnect: Date.now() } : a));
        dispatch(setAttendeeMessaging({ ...messaging, attendees: _attendees }));
      }
    });

    eventChannel?.bind('user-disconnect', (data: { userId: string }) => {
      if (data.userId === attendee?._id) {
        const _attendees = messaging?.attendees?.map((a) => (a._id === data.userId ? { ...a, datetimeLastRealtimeDisconnect: Date.now() } : a));
        dispatch(setAttendeeMessaging({ ...messaging, attendees: _attendees }));
      }
    });
  }, []);

  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTo({ top: conversationRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [sortedMessages]);

  const onFileUpload = (files?: File[]) => {
    if (!files) return;
    setAttachments([...attachments, ...files]);
    fileInputRef.current.value = null;
  };

  const removeAttachment = (index: number, fileId?: string) => {
    const _attachments = [...attachments];
    _attachments.splice(index, 1);
    setAttachments(_attachments);
    if (fileId) setFileIds(fileIds.filter((id) => id === fileId));
  };

  const headerButtons = (showLabel = false) => {
    return (
      <>
        {!meeting && (
          <div className="flex items-center mr-4">
            <AttendeeCallButton calleeId={_attendee?._id} name={_attendee.name} />
            {showLabel && <p>{t('meeting:on-site-call.call')}</p>}
          </div>
        )}
        {registration && !!event?.modules.messagingCenter.allowContactShare && (
          <VyConfirm
            message={t('event:messaging.contactshare-confirm-message', { attendee: _attendee.name })}
            onConfirm={() => sendMessage(undefined, 'VCARD')}>
            <VyTooltip title={t('event:messaging.contactshare-tooltip')}>
              <div className="flex items-center mr-4">
                <IconButton size="large">
                  <FaHandshake />
                </IconButton>
                {showLabel && <p>{t('account:challenges.contact-share')}</p>}
              </div>
            </VyTooltip>
          </VyConfirm>
        )}
        {!isUserEventHost(attendee as any) && (
          <div className="flex items-center mr-4" onClick={() => showReportModal(attendee?._id, attendee?.name)}>
            <IconButton size="large">
              <ReportRounded />
            </IconButton>
            {showLabel && <p>{t('event:report-user-short')}</p>}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex flex-col w-full h-full">
      <div className="flex flex-row items-center space-x-0 w-full max-w-full border-b-2 sm:space-x-2" style={{ height: '10%' }}>
        {showHeaderButtons && (
          <IconButton onClick={() => onReturnClick?.()} size="large">
            <ArrowBackIos />
          </IconButton>
        )}
        <div className="flex flex-row items-center ml-4 space-x-2 w-full h-full">
          <AttendeeAvatar attendee={_attendee} showStatus showStatusEmoji />
          <span className="truncate">{_attendee?.name}</span>
        </div>

        {showHeaderButtons && (
          <>
            <div className="hidden sm:flex">{headerButtons()}</div>
            <div className="flex sm:hidden">
              <VyPopoverMenu content={() => <>{headerButtons(true)}</>}>
                <IconButton size="large">
                  <MoreVert />
                </IconButton>
              </VyPopoverMenu>
            </div>
          </>
        )}
      </div>
      <div className="flex flex-col-reverse px-4 pb-4 w-full grow" style={{ height: '90%' }}>
        {!registration?.messagingGhostMode && !(isBanned || isReported) && (
          <div className="flex flex-col justify-center items-center w-full">
            <form className="flex flex-row space-x-0 w-full h-full sm:space-x-2" onSubmit={handleSubmit(submit)}>
              <TextField
                fullWidth
                placeholder={t('event:messaging.write-message')}
                inputProps={{ maxLength: 200 }}
                variant="outlined"
                {...register('message')}
                inputRef={(ref) => {
                  msgInputRef.current = ref;
                }}
                className="my-2"
              />
              <div className="flex justify-center items-center h-full">
                <ChatEmojis chatInputRef={msgInputRef} iconColor="rgba(59, 130, 246, 0.5)" onChange={(value) => setValue('message', value)} />
              </div>
              <div className="flex justify-center items-center h-full">
                <VyTooltip title={t('event:messaging.attach-files')}>
                  <IconButton size="small">
                    <label id="conversation-file" className="cursor-pointer">
                      <input
                        ref={fileInputRef}
                        id="conversation-file"
                        style={{ display: 'none' }}
                        type="file"
                        multiple
                        // @ts-ignore
                        onChange={(e) => onFileUpload([...e.target?.files])}
                      />

                      <Attachment />
                    </label>
                  </IconButton>
                </VyTooltip>
              </div>
              <div className="grid place-items-center h-full">
                <IconButton
                  type="submit"
                  color="primary"
                  disabled={(!watch('message') && fileIds.length === 0) || fileIds.length !== attachments.length}
                  size="small">
                  <Send />
                </IconButton>
              </div>
            </form>
            {attachments.length > 0 && (
              <div className="flex flex-row flex-wrap px-4 w-full">
                {attachments.map((att, i) => (
                  <MessagingAttachment
                    key={`attachment-${i}`}
                    attachment={att}
                    onUploadFinish={(fileId) => setFileIds([...fileIds, fileId])}
                    onRemove={(fileId) => removeAttachment(i, fileId)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {isBanned || isReported ? (
          <>
            {isBanned && (
              <div className="grid place-items-center w-full h-1/6">
                <p className="italic text-slate-400">{t('event:messaging.banned-message')}</p>
              </div>
            )}

            {isReported && (
              <div className="grid place-items-center w-full h-1/6">
                <p className="italic text-slate-400">{t('event:messaging.reported-message')}</p>
              </div>
            )}
          </>
        ) : (
          <>
            {registration?.messagingGhostMode && (
              <div className="grid place-items-center w-full h-1/6">
                <p className="italic text-slate-400">{t('event:messaging.ghostmode-conversation-message')}</p>
              </div>
            )}
          </>
        )}

        {sortedMessages && sortedMessages.length > 0 ? (
          <div className="flex overflow-auto flex-col-reverse py-4 w-full h-5/6 grow" style={{ height: '90%' }}>
            {sortedMessages?.map((message) => (
              <Message
                key={message?._id}
                message={message?.message}
                messageId={message?._id}
                messageType={message?.messageType}
                date={message?.datetime_created}
                ended={message?.datetime_ended}
                isError={message.isError}
                pending={message.isPending && messaging?.messages && messaging?.messages.filter((m) => m.isPending).length > 1}
                isOwn={message?.sender === user?._id}
                attendee={_attendee}
                files={message.files}
                onResend={() => {
                  if (!messaging) return;
                  const pendingMessages = messaging?.messages?.filter((m) => m.isPending);
                  const _messages = [
                    {
                      ...message,
                      _id: `pending-${pendingMessages?.length || 0}`,
                      isPending: true,
                      isError: false,
                      datetime_created: addHours(new Date(), pendingMessages?.length || 0),
                    },
                    ...(messaging?.messages?.filter((m) => m._id !== message._id) || []),
                  ];
                  dispatch(setAttendeeMessaging({ ...messaging, messages: _messages }));
                }}
              />
            ))}
          </div>
        ) : (
          <div className="grid place-items-center w-full h-full">
            <div className="flex flex-col items-center space-y-2">
              <p className="text-lg italic text-slate-500">{t('event:messaging.empty-conversation-text-1')}</p>
              <p className="text-lg font-bold text-slate-800">{t('event:messaging.empty-conversation-text-2')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default ConversationComponent;
